import { MathUtils, Quaternion, Vector3 } from 'three';

// a pool of objects so we don't need to allocate them every frame
const tmpQuat = new Quaternion();
const rotQuat = new Quaternion();
const axisX = new Vector3(1, 0, 0);
const axisY = new Vector3(0, 1, 0);

/**
 * SatelliteController is an orbit controller that auto-spins while also providing a way to hone in on a single point
 */
export default class SatelliteController {
  constructor(camera, globe, domElement) {
    this.latitude = 0.0;
    this.longitude = 0.0;
    this.rotateSensitivity = 25.0;
    this.movementSmoothing = 0.5;
    // the value used to overwrite movementSmoothing when the user starts rotating. This allows changing movement
    // smoothing when auto-focussing until the user interacts.
    this.interactiveMovementSmoothing = 0.5;
    this.rotationAmount = 0.0; // signals the app about how much was rotated this frame. Accumulates
    this.zoomFactorSmoothing = 0.9;
    this.container = domElement || document;
    this.autoRotationSpeed = -4.0;
    this.camera = camera;
    this.globe = globe;
    this.interactive = false;

    this.maxRadius = 5.5;
    this.minRadius = 1.7;
    this._zoomFactor = 0.0;
    this._previousZoomFactor = 0.0;
    this._smoothedZoom = 0.0;
    this._snapToPosition = true;

    this._smoothedRadius = this.maxRadius;
    this._userRotating = false;

    this._oldX = 0;
    this._oldY = 0;
    this._pinchStartDistance = 0;
    this._pinchStartRadius = 0;

    this._scrollLineHeight = getScrollLineHeight();

    // TODO: [SS2-747] set to false in the actual app because we'll actually be scrolling
    this.allowMouseWheel = true;
    this.mouseWheelSensitivity = 1.0 / 1000.0;

    this.onUserRotation = null;
  }

  get zoomFactor() {
    return this._zoomFactor;
  }

  set zoomFactor(value) {
    this._zoomFactor = MathUtils.clamp(value, 0, 1);
  }

  update(dt) {
    // handle camera through rotation first, this allows easier interpolation using Quaternions
    this.longitude += this.autoRotationSpeed * dt;

    // first calculate the free moving rotation
    this._latLongRotation(this.latitude, this.longitude, rotQuat);

    const smoothedQuat = this.globe.quaternion;

    // looking at the motion from Pablo, the rotation happens in between
    if (this._snapToPosition) {
      smoothedQuat.copy(rotQuat);
      this._smoothedZoom = this._zoomFactor;
    } else {
      this._smoothedZoom = MathUtils.lerp(
        this._zoomFactor,
        this._smoothedZoom,
        this.zoomFactorSmoothing
      );

      smoothedQuat.slerp(rotQuat, 1.0 - this.movementSmoothing);
    }

    const radius = MathUtils.lerp(this.maxRadius, this.minRadius, this._smoothedZoom);
    this.camera.position.z = radius;

    this._snapToPosition = false;

    // shrink back rotation communication to 0
    this.rotationAmount *= 0.5;
  }

  startInteraction() {
    this.interactive = true;
    // TODO: Consider using pointer events, but it seems to just move the duplication elsewhere
    this.container.addEventListener('mousedown', this._onMouseDown);
    document.addEventListener('mouseup', this._onMouseUp);
    document.addEventListener('mousemove', this._onMouseMove);
    this.container.addEventListener('touchstart', this._onTouchStart, { passive: true });
    document.addEventListener('touchend', this._onTouchEnd);
    document.addEventListener('touchmove', this._onTouchMove, { passive: true });

    if (this.allowMouseWheel) {
      this.container.addEventListener('wheel', this._onMouseWheel, { passive: true });
    }
  }

  stopInteraction() {
    this.interactive = false;
    this.container.removeEventListener('mousedown', this._onMouseDown);
    document.removeEventListener('mouseup', this._onMouseUp);
    document.removeEventListener('mousemove', this._onMouseMove);
    this.container.removeEventListener('touchstart', this._onTouchStart);
    document.removeEventListener('touchend', this._onTouchEnd);
    document.removeEventListener('touchmove', this._onTouchMove);
    this.container.removeEventListener('wheel', this._onMouseWheel);
  }

  _onMouseDown = event => {
    this._startUserRotation();
    this._oldX = event.clientX;
    this._oldY = event.clientY;
  };

  _onMouseUp = () => {
    this._userRotating = false;
  };

  _onMouseMove = event => {
    if (!this._userRotating) return;
    this._updateMove(event.clientX, event.clientY);
  };

  _onMouseWheel = event => {
    let delta = event.deltaY;
    // firefox reports line heights
    if (event.deltaMode === 1) delta *= this._scrollLineHeight;
    this.zoomFactor -= delta * this.mouseWheelSensitivity;
    return false;
  };

  _onTouchStart = event => {
    this._startUserRotation();
    const touch = event.touches[0];
    this._oldX = touch.clientX;
    this._oldY = touch.clientY;

    if (event.touches.length === 2) {
      const touch2 = event.touches[1];
      const dx = touch.screenX - touch2.screenX;
      const dy = touch.screenY - touch2.screenY;
      this._pinchStartDistance = Math.sqrt(dx * dx + dy * dy);
      this._pinchStartRadius = this._smoothedRadius;
    }
  };

  _onTouchEnd = event => {
    if (event.touches.length === 1) {
      // the new single touch should be the new reference position to prevent a sudden snap in rotation after zooming
      const touch = event.touches[0];
      this._oldX = touch.clientX;
      this._oldY = touch.clientY;
    }
    if (event.touches.length === 0) this._userRotating = false;
    this._previousZoomFactor = this.zoomFactor;
  };

  _onTouchMove = event => {
    // should not allow rotating once we're zooming
    if (!this._userRotating) return;
    const touch = event.touches[0];

    if (event.touches.length === 1) {
      this._updateMove(touch.clientX, touch.clientY);
    } else if (event.touches.length >= 2) {
      const touch2 = event.touches[1];
      const dx = touch.screenX - touch2.screenX;
      const dy = touch.screenY - touch2.screenY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      const radius = (this._pinchStartRadius * this._pinchStartDistance) / dist;
      this.zoomFactor =
        this._previousZoomFactor +
        1 -
        (radius - this.minRadius) / (this.maxRadius - this.minRadius);
    }

    return false;
  };

  _updateMove(x, y) {
    const dx = x - this._oldX;
    const dy = y - this._oldY;

    const sensitivity =
      (this.rotateSensitivity * Math.abs(this.camera.position.z)) / window.innerHeight;
    this.longitude -= dx * sensitivity;
    this.latitude += dy * sensitivity;
    // prevent
    this.latitude = MathUtils.clamp(this.latitude, -89, 89);

    this._oldX = x;
    this._oldY = y;

    this.rotationAmount += Math.sqrt(dx * dx + dy * dy) * sensitivity;

    if (this.onUserRotation) this.onUserRotation(this.latitude, this.longitude);
  }

  _latLongRotation(lat, long, quat) {
    // + 90 to correct for ThreeJS' sphere orientation
    tmpQuat.setFromAxisAngle(axisY, -(90 + long) * MathUtils.DEG2RAD);
    quat.setFromAxisAngle(axisX, lat * MathUtils.DEG2RAD);
    quat.multiply(tmpQuat);
  }

  _startUserRotation() {
    this._userRotating = true;
    this.movementSmoothing = this.interactiveMovementSmoothing;
  }

  snapToPosition() {
    this._snapToPosition = true;
  }

  getSettingsState(target) {
    target.lat = this.latitude;
    target.long = this.longitude;
    target.zoomFactor = this.zoomFactor;
    target.zoomFactorSmoothing = this.zoomFactorSmoothing;
    target.rotationSpeed = this.autoRotationSpeed;
  }
}

// https://stackoverflow.com/questions/20110224/what-is-the-height-of-a-line-in-a-wheel-event-deltamode-dom-delta-line
function getScrollLineHeight() {
  let r;
  const iframe = document.createElement('iframe');
  iframe.src = '#';
  document.body.appendChild(iframe);
  const iwin = iframe.contentWindow;
  const idoc = iwin.document;
  idoc.open();
  idoc.write('<!DOCTYPE html><html><head></head><body><span>a</span></body></html>');
  idoc.close();
  const span = idoc.body.firstElementChild;
  r = span.offsetHeight;
  document.body.removeChild(iframe);
  return r;
}
